/**
* Nom : atReportList.js
* Description : Ce fichier contient la définition de la page atReportList 
*               Cette page liste les rapports interne Asclepios Tech avec un filtrage
* Date de création : 26 septembre 2024
*/

/* CSS et assets */
import { AddIcon } from 'package_ui_ihm/dist'
import { EditIcon } from 'package_ui_ihm/dist'
import { DeleteIcon } from 'package_ui_ihm/dist'
import { FileIcon } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import { TabRD } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { Item } from 'package_ui_ihm/dist'
import { InputFormSide } from 'package_ui_ihm/dist'
import { SelectFormSide } from 'package_ui_ihm/dist'
import { ListFilter } from 'package_ui_ihm/dist'
import { TextFilter } from 'package_ui_ihm/dist'
import Divider from '@mui/material/Divider';
import { saveAs } from "file-saver";

/* Hook */
import { useState, useEffect } from 'react';
import { useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist';
import { useApi } from 'package_ui_ihm/dist';
import { useScreenWidth } from 'package_ui_ihm/dist';


/**
* ATReportList : affiche la page qui liste les rapports interne Asclepios Tech
*                filtrage de la liste possible selon la catégorie, l'année et l'objectif de l'étude
*                un lien permet d'afficher un formulaire pour créer un nouveau rapport
*                pour chaque rapport une icone permet de le supprimer
*
* Type : page 
*
* Parametres : 
*  None
*/
function ATReportList() {

  // Define useState hooks

  const [reports, setReports] = useState([])
  const [statusReport, setStatusReport] = useState(null)
  const [isDataLoading, setDataLoading] = useState(false)
  const [isCreation, setCreation] = useState(false)
  const [isUpdate, setUpdate] = useState(false)
  const [idUpdated, setIdUpdated] = useState(null)
  const [internalReference, setInternalReference] = useState(null);
  const [categoryId, setCategoryId] = useState(0);
  const [year, setYear] = useState(null);
  const [objective, setObjective] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [newKeywords, setNewKeywords] = useState(null);
  const [projectId, setProjectId] = useState(0);
  const [internalReferenceError, setInternalReferenceError] = useState(false);
  const [categoryIdError, setCategoryIdError] = useState(false);
  const [yearError, setYearError] = useState(false);
  const [objectiveError, setObjectiveError] = useState(false);
  const [keywordsError, setKeywordsError] = useState(false);
  const [projectIdError, setProjectIdError] = useState(false);
  const [selectedCat, setSelectedCat] = useState(0);
  const [categories, setCategories] = useState([])
  const [projects, setProjects] = useState([])
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedProject, setSelectedProject] = useState(0);
  const [years, setYears] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState("");
  const [selectedInternalReference, setSelectedInternalReference] = useState("");
  const [filteredTempReports, setFilteredTempReports] = useState([]); 
  const [filteredReports, setFilteredReports] = useState(reports); 
  const [isFormClicked, setFormClicked] = useState(false);


  // Define useContext hooks
  const { authStatus, authToken, currentUser } = useAuth();
  const { path } = useApi();


  // Define useBlocker hooks
  const shouldBlock = isCreation;
  const blocker = useBlocker(shouldBlock);


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();
  

  // Define functions
  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
      accept: 'application/json',
      Authorization: token,
    }
    // Requests in parallel to win time 
    const [resCategory, resProject, resReport] = await Promise.all([
      // Categories
      fetch(path + "/api/bo-cary/", {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }),
      // Projects
      fetch(path + "/api/bo-prct/", {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }),
      // Reports
      fetch(path + "/api/bo-atre/", {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }),
    ])
    // Responses in parallel to win time
    const [dataCategory, dataProject, dataReport] = await Promise.all([
      resCategory.json(),
      resProject.json(),
      resReport.json(),
    ])
    // Set variables
    setCategories(dataCategory["results"])
    setProjects(dataProject["results"])
    setStatusReport(resReport["status"])
    const report_list = dataReport["results"]
    setReports(report_list)
    // Year des reports 
    const year_list = [...new Set(report_list.map((report) => (report.year)))]
    const year_dict = year_list.map((year, index) => ({"id": index + 1, "value": year}))
    setYears(year_dict)
    // Dataloading
    setDataLoading(false)
  }

  const handleClick = (event) => {
    event.preventDefault();
    setCreation(true)
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormClicked(true)
    if(internalReference === null || internalReference === ''){
      setInternalReferenceError(true)
    }
    else
    {   
      setInternalReferenceError(false)
    }
    if(categoryId === 0 || categoryId === '0'){
      setCategoryIdError(true)
    }
    else
    {   
      setCategoryIdError(false)
    }
    if(year === null || year === ''){
      setYearError(true)
    }
    else
    {   
      setYearError(false)
    }
    if(objective === null || objective === ''){
      setObjectiveError(true)
    }
    else
    {   
      setObjectiveError(false)
    }
    if(keywords === null || keywords === ''){
      setKeywordsError(true)
    }
    else
    {   
      setKeywordsError(false)
    }
    if(projectId === 0 || projectId === '0'){
      setProjectIdError(true)
    }
    else
    {   
      setProjectIdError(false)
    }
  };

  const handleReset = (event) => {
    event.preventDefault();
    setCreation(false)
    setInternalReference(null)
    setCategoryId(0)
    setYear(null)
    setObjective(null)
    setKeywords(null)
    setProjectId(0)
    setInternalReferenceError(false)
    setCategoryIdError(false)
    setYearError(false)
    setObjectiveError(false)
    setKeywordsError(false)
    setProjectIdError(false)
  };

  const filterReset = (event) => {
      event.preventDefault();
      setFilteredReports(reports)
      setSelectedObjective("")
      setSelectedCat(0)
      setSelectedYear(0)
      setSelectedKeywords("")
      setSelectedProject(0)
  }

  async function sendData(internalReference, category_id, year, objective, keywords, project_id) {
    // Token
    const token = "Bearer " + authToken
    // Create at report
    await fetch(path + "/api/bo-atre/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "internal_reference": internalReference,
            "category_id": category_id,
            "year": year,
            "objective": objective,
            "keywords": keywords,
            "project_id": project_id
        })
      }
    );
    setCreation(false)
    setInternalReference(null)
    setCategoryId(0)
    setYear(null)
    setObjective(null)
    setKeywords(null)
    setProjectId(0)
    retrieveData()
    setFormClicked(false)
  }

  const handleEditIcon = (event, report) => {
    event.preventDefault();
    setIdUpdated(report['id'])
    setNewKeywords(report['keywords'])
    setUpdate(true)
  };
  
  const updateSubmit = (event) => {
    event.preventDefault();
    sendUpdate(newKeywords)
  };

  const updateReset = (event) => {
    event.preventDefault();
    setNewKeywords(null)
    setIdUpdated(null)
    setUpdate(false)
  };

  async function sendUpdate(keywords) {
    // Token
    const token = "Bearer " + authToken
    // Update at report
    await fetch(path + "/api/bo-atre/" + idUpdated + "/", 
      {
        method: 'PATCH',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "keywords": keywords
        })
      }
    );
    setNewKeywords(null)
    setIdUpdated(null)
    retrieveData()
    setUpdate(false)
  }

  const handleRemoveIcon = (event, id) => {
    event.preventDefault();
    sendDelete(id)
  };

  async function sendDelete(id) {
    // Token
    const token = "Bearer " + authToken
    // Delete at_report 
    await fetch(path + "/api/bo-atre/" + id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        }
      }
    );
    retrieveData()
  }

  function retrieve_param_object_from_id(list, id) {
    return (list.find((item) => (item['id'] === Number(id))))['value']
  }

  function updateFilteredReports() {
    // Filter category
    if( Number(selectedCat) > 0 
        && Number(selectedYear) === 0
        && selectedObjective === "")
    {
      setFilteredTempReports(reports.filter((report) => report.category.id === Number(selectedCat)))
    } 
    // Filter year
    else if( Number(selectedCat) === 0 
              && Number(selectedYear) > 0
              && selectedObjective === "")
    {
      setFilteredTempReports(reports.filter((report) => report.year === retrieve_param_object_from_id(years, selectedYear)))
    } 
    // Filter objective
    else if( Number(selectedCat) === 0 
              && Number(selectedYear) === 0
              && selectedObjective !== "")
    {
      setFilteredTempReports(reports.filter((report) => report.objective.toLowerCase().includes(selectedObjective.toLowerCase())))
    } 
    // Filter category and year
    else if( Number(selectedCat) > 0 
              && Number(selectedYear) > 0
              && selectedObjective === "")
    {
      setFilteredTempReports(reports.filter((report) => report.category.id === Number(selectedCat) 
                                                        && report.year === retrieve_param_object_from_id(years, selectedYear)))
    } 
    // Filter category and objective
    else if( Number(selectedCat) > 0 
              && Number(selectedYear) === 0
              && selectedObjective !== "")
    {
      setFilteredTempReports(reports.filter((report) => report.category.id === Number(selectedCat) 
                                                        && report.objective.toLowerCase().includes(selectedObjective.toLowerCase())))
    } 
    // Filter year and objective
    else if( Number(selectedCat) === 0 
              && Number(selectedYear) > 0
              && selectedObjective !== "")
    {
      setFilteredTempReports(reports.filter((report) => report.year === retrieve_param_object_from_id(years, selectedYear) 
                                                        && report.objective.toLowerCase().includes(selectedObjective.toLowerCase())))
    } 
    // Filter category and year and objective
    else if( Number(selectedCat) > 0 
              && Number(selectedYear) > 0
              && selectedObjective !== "")
    {
      setFilteredTempReports(reports.filter((report) => report.category.id === Number(selectedCat) 
                                                        && report.year === retrieve_param_object_from_id(years, selectedYear)
                                                        && report.objective.toLowerCase().includes(selectedObjective.toLowerCase())))
    } 
    // No filter
    else
    {
      setFilteredTempReports(reports)
    }
  }

  function updateFilteredReportsByKeywords() {
    // Filter keywords in keywords field and abstract field
    if(selectedKeywords !== ""
        && Number(selectedProject) === 0
        && selectedInternalReference === "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.keywords.toLowerCase().includes(selectedKeywords.toLowerCase())))
    } 
    // Filter project
    else if(selectedKeywords === ""
            && Number(selectedProject) > 0
            && selectedInternalReference === "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.project.id === Number(selectedProject)))
    } 
    // Filter internal reference
    else if(selectedKeywords === ""
            && Number(selectedProject) === 0
            && selectedInternalReference !== "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.internal_reference.toLowerCase().includes(selectedInternalReference.toLowerCase())))
    } 
    // Filter keywords in keywords field and abstract field and project
    else if(selectedKeywords !== ""
            && Number(selectedProject) > 0
            && selectedInternalReference === "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.keywords.toLowerCase().includes(selectedKeywords.toLowerCase())
                                                                && report.project.id === Number(selectedProject)))
    } 
    // Filter keywords in keywords field and abstract field and internal reference
    else if(selectedKeywords !== ""
            && Number(selectedProject) === 0
            && selectedInternalReference !== "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.keywords.toLowerCase().includes(selectedKeywords.toLowerCase())
                                                                && report.internal_reference.toLowerCase().includes(selectedInternalReference.toLowerCase())))
    } 
    // Filter project and internal reference
    else if(selectedKeywords === ""
            && Number(selectedProject) > 0
            && selectedInternalReference !== "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.project.id === Number(selectedProject)
                                                                && report.internal_reference.toLowerCase().includes(selectedInternalReference.toLowerCase())))
    } 
    // Filter keywords in keywords field and abstract field and project and internal reference
    else if(selectedKeywords !== ""
            && Number(selectedProject) > 0
            && selectedInternalReference !== "")
    {
      setFilteredReports(filteredTempReports.filter((report) => report.keywords.toLowerCase().includes(selectedKeywords.toLowerCase())
                                                                && report.project.id === Number(selectedProject)
                                                                && report.internal_reference.toLowerCase().includes(selectedInternalReference.toLowerCase())))
    } 
    // No filter
    else
    {
      setFilteredReports(filteredTempReports)
    }
  }
  
  const handleExport = (event) => {
    event.preventDefault();
    exportToBibTex();
  };

  function exportToBibTex() {
    var merged_content = ""
    filteredReports.map((report) => (
      merged_content += `
        @report{${report['category']['name']}${report['year']},
        internal_reference = {${report['internal_reference']}},
        category = {${report['category']['name']}},
        year = {${report['year']}},
        objective = {${report['objective']}},
        keywords = {${report['keywords']}},
        project = {${report['project']['name']}}
        }
      `)
    )
    const blob = new Blob([merged_content], { type: "text/plain" });
    saveAs(blob, "rapports.bib");
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(internalReferenceError === false && categoryIdError === false && yearError === false && objectiveError === false && keywordsError === false && projectIdError === false && isFormClicked === true){
      sendData(internalReference, categoryId, year, objective, keywords, projectId)
    } else {
      setFormClicked(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalReferenceError, isFormClicked])

  useEffect(() => {
    updateFilteredReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, selectedCat, selectedYear, selectedObjective])

  useEffect(() => {
    updateFilteredReportsByKeywords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTempReports, selectedKeywords, selectedProject, selectedInternalReference])

  useEffect(() => {
      // Bloquer la fermeture d'onglet/rafraîchissement
      const handleBeforeUnload = (e) => {
      if (shouldBlock === true) {
          // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
          e.preventDefault();
          e.returnValue = "";
          // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
          return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
      }
      }; 

      window.addEventListener("beforeunload", handleBeforeUnload);

      // Nettoyer l'écouteur quand le composant est démonté
      return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock]);

  useEffect(() => {
      if (blocker.state === "blocked") {
          const confirmed = window.confirm(
              "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
          );

          if (confirmed) {
              blocker.proceed();
          } else {
              blocker.reset();
          }
      }
  }, [blocker]);


  // Render component
  return (
    <div>
      <TabRD tabNumber={4}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <p>Chargement des données</p>
          ) : (
            (statusReport === 200) ? (
              <div>
                <h1>Rapports internes</h1>  
                <div className={(screenWidth > 650) ? 'grid-1-3' : null}>
                  {/*New*/}
                  <div className='flex-column'>
                    <div className='link' onClick={handleClick}>
                      <img src={AddIcon} alt='add_item_icon' className='link-img' />
                      <div className='link-text'>Rapport</div>
                    </div>
                    {(isCreation === true) ? (
                      <div className='flex-rows'>
                        <h3>Nouveau rapport</h3>
                        <form onSubmit={handleSubmit} onReset={handleReset} className='flex-column'>
                          <div className='flex-column form-center'>
                            <InputFormSide error={internalReferenceError} type="text" label="Référence interne" value={internalReference} setValue={setInternalReference}/>
                            <SelectFormSide error={categoryIdError} labelText='Categorie' choices={categories.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={categoryId} setSelectedItem={setCategoryId}/>
                            <InputFormSide error={yearError} type="text" label="Année" value={year} setValue={setYear}/>
                            <InputFormSide error={objectiveError} type="text" label="Objectif de l'étude" value={objective} setValue={setObjective}/>
                            <InputFormSide error={keywordsError} type="text" label="Mots clés" value={keywords} setValue={setKeywords}/>
                            <SelectFormSide error={projectIdError} labelText='Projet' choices={projects.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={projectId} setSelectedItem={setProjectId}/>
                          </div>
                          <div className='flex-row form-center'>
                            <button type="reset" className='button'>Annuler</button>
                            <button type="submit" className='button'>Valider</button>
                          </div>
                        </form> 
                      </div>
                    ) : (
                      <div></div>
                    )}
                    {/*Filtering*/}
                    <h3>Filtres</h3>
                    <TextFilter label="Référence interne" value={selectedInternalReference} setValue={setSelectedInternalReference}></TextFilter>
                    <ListFilter labelText='Catégorie' choices={categories.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedCat} setSelectedItem={setSelectedCat} subfilter={false}/>
                    <ListFilter labelText='Année' choices={years.sort((a, b) => (a.value > b.value)?1:-1)} choiceParam={'value'} selectedItem={selectedYear} setSelectedItem={setSelectedYear} subfilter={false}/>
                    <TextFilter label="Objectif de l'étude" value={selectedObjective} setValue={setSelectedObjective}></TextFilter>
                    <TextFilter label="Mot clé" value={selectedKeywords} setValue={setSelectedKeywords}></TextFilter>
                    <ListFilter labelText='Projet' choices={projects.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedProject} setSelectedItem={setSelectedProject} subfilter={false}/>
                    <div>
                      <button onClick={filterReset} className='button-secondary clickable'>Sans filtres</button>
                    </div>
                    {/*Export*/}
                    <h3>Export</h3>
                    <div onClick={handleExport} className='link clickable'>
                      <img src={FileIcon} alt='file_icon' className='link-img' />
                      <div className='link-text'>Exporter au format BibTex</div>
                    </div>
                  </div>
                  {(screenWidth > 650) ? null : <Divider/>}
                  {/*AT reports list*/}
                  <div className='scroll-list-rd'>
                    <h2 className='subtitle'>Nombre de rapports internes : {filteredReports.length}</h2>
                    {(filteredReports && filteredReports.length !== 0) ? (
                      filteredReports.sort((a, b) => (a.internal_reference > b.internal_reference)?1:-1).map((report) => (
                        <Item key={report['id']} id={report['id']}>
                          <div className='grid-1-1'>
                            <h3 className='item-text'>{report['internal_reference']}</h3>
                            <div className='flex-row justify-end'>
                              <div/>
                              {<img src={EditIcon} alt='edit_item_icon' onClick={(e) => handleEditIcon(e, report)} className='link-img link-icon' />}
                              { (currentUser["role_name"] === "AT_ADMIN") ? (
                                  <img src={DeleteIcon} alt='delete_item_icon' onClick={(e) => handleRemoveIcon(e, report['id']) } className='link-img'/>
                                ) : (
                                  <p/>
                                )
                              }
                            </div>
                          </div>
                          <div className='grid-1-1'>
                            <p className='item-text'><strong>Catégorie :</strong> {report['category']['name']}</p>
                            <p className='item-text'><strong>Année :</strong> {report['year']}</p>
                          </div>
                          <p className='item-text'><strong>Objectif de l'étude :</strong> {report['objective']}</p>
                          <p className='item-text'><strong>Projet :</strong> {report['project']['name']}</p>
                          <p className='item-text'><strong>Mots clés :</strong> {report['keywords']}</p>
                          {(isUpdate === true && report['id'] === idUpdated) ? (
                            <form onSubmit={updateSubmit} onReset={updateReset} className='flex-row justify-end'>
                              <input className='form-input' type="text" value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)}/>
                              <button type="reset" className='button'>Annuler</button>
                              <button type="submit" className='button'>Valider</button>
                            </form> 
                          ) : (
                            <div></div>
                          )}
                        </Item>
                      ))
                    ) : (
                      <p>Aucun rapport interne actuellement. Veuillez en créer un !</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <h1>Connexion failed</h1>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default ATReportList;
